// TimeseriesChart — the marquee combined chart, GENERIC over the series array,
// so the whole Tier-1 timeseries family renders with zero extra code. Every
// series shares ONE time x-axis and ONE y-axis (single unit per the contract)
// with a single crosshair + tooltip.
//
// Series treatment:
//   - kind 'bar'  -> Histogram, per-point diverging colour (blue >= 0 / red < 0).
//   - kind 'line', SINGLE line in the report -> Baseline with the diverging fill
//     (blue above / red below) relative to the reference (or 0) — the net_flow
//     cumulative look. This is the signed-quantity case (cum_net, buy_share_pct).
//   - kind 'line', 2+ lines -> plain distinct-coloured LineSeries + a legend, no
//     fill (a comparison report: pcr_premium+pcr_count, cum_btc+cum_eth).
//
// Extras:
//   - meta.reference (optional) -> a muted dashed horizontal reference line, and
//     the baseline the single diverging line fills against.
//   - y_format drives the axis + tooltip formatter (usd/count/ratio/pct/index).
//   - y_format 'index' -> symmetric y-axis around 0 (±max|v|).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  LineStyle,
  UTCTimestamp,
  ColorType,
  CrosshairMode,
} from 'lightweight-charts';

import { useThemeMode } from '../../../../providers/theme-provider';
import { getTokenValue, tokens } from '../../../../styles/tokens';
import {
  formatFlowValue,
  isoToChartTime,
} from '../../../../providers/options-flow/options-flow.helpers';
import { TimeseriesSeries } from '../../../../providers/options-flow/types';

interface TimeseriesChartProps {
  series: TimeseriesSeries[];
  yFormat: string;
  reference?: number;
  // "hourly" -> show the time on the axis + crosshair; "daily" -> date only, so
  // a long window (YTD) reads as dates rather than thousands of hourly ticks.
  granularity?: 'hourly' | 'daily';
  height?: number;
}

// Distinct hues for multi-line comparison reports (kept away from the buy/sell
// diverging pair, which is reserved for the single signed-quantity line).
const LINE_PALETTE_TOKENS = [
  'charts.lightBlue',
  'charts.purple',
  'charts.turquoise',
  'charts.orange',
  'charts.pink',
];

interface TooltipRow {
  label: string;
  value: string;
}

interface TooltipState {
  x: number;
  y: number;
  date: string;
  rows: TooltipRow[];
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const fmtTooltipDate = (time: UTCTimestamp, withTime: boolean): string => {
  const d = new Date((time as number) * 1000);
  const date = `${String(d.getUTCDate()).padStart(2, '0')} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  return withTime ? `${date} ${String(d.getUTCHours()).padStart(2, '0')}:00` : date;
};

// A plotted point OR a whitespace point (time only) that GAPS the line. Thin/alt
// tokens carry null `v` in empty buckets; passing null into the chart lib throws,
// so those become whitespace points (the series simply breaks there).
type ChartPoint = { time: UTCTimestamp; value: number } | { time: UTCTimestamp };

const hasValue = (d: ChartPoint): d is { time: UTCTimestamp; value: number } => 'value' in d;

const toChartData = (s: TimeseriesSeries): ChartPoint[] =>
  s.points
    .map((p): ChartPoint | null => {
      const time = isoToChartTime(p.t);
      if (time == null) return null;
      const t = time as UTCTimestamp;
      return Number.isFinite(p.v as number) ? { time: t, value: p.v as number } : { time: t };
    })
    .filter((d): d is ChartPoint => d !== null);

export const TimeseriesChart = ({
  series,
  yFormat,
  reference,
  granularity = 'hourly',
  height = 260,
}: TimeseriesChartProps) => {
  const hourly = granularity === 'hourly';
  const { resolvedMode } = useThemeMode();
  const container = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi>();
  const seriesRef = useRef<{ label: string; api: ISeriesApi<'Baseline' | 'Histogram' | 'Line'> }[]>(
    [],
  );
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const lineSeries = useMemo(() => series.filter((s) => s.kind === 'line'), [series]);
  const isMultiLine = lineSeries.length >= 2;

  // Deterministic colour per multi-line series (label -> hex), reused by the
  // legend so swatches match the plotted lines.
  const lineColors = useMemo(() => {
    const palette = LINE_PALETTE_TOKENS.map((t) => getTokenValue(t, resolvedMode));
    const map = new Map<string, string>();
    lineSeries.forEach((s, i) => map.set(s.label, palette[i % palette.length]));
    return map;
  }, [lineSeries, resolvedMode]);

  useEffect(() => {
    if (!container.current) return;

    const buy = getTokenValue('flow.buy', resolvedMode);
    const sell = getTokenValue('flow.sell', resolvedMode);
    const grid = getTokenValue('border.chartGrid', resolvedMode);
    const axisText = getTokenValue('text.chartAxis', resolvedMode);
    const refColor = getTokenValue('text.muted', resolvedMode);

    const chart = createChart(container.current, {
      width: container.current.clientWidth,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: axisText,
      },
      grid: { vertLines: { color: grid }, horzLines: { color: grid } },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { borderColor: grid, timeVisible: hourly, secondsVisible: false },
      rightPriceScale: { autoScale: true, borderColor: grid },
      localization: { priceFormatter: (p: number) => formatFlowValue(p, yFormat) },
    });
    chartRef.current = chart;

    // Symmetric ±max|v| axis for index units (shared across every series). Nulls
    // are excluded from the reduce — a Math.abs/Math.max over null/undefined
    // would poison the domain (NaN) and crash the scale.
    const maxAbs =
      yFormat === 'index'
        ? Math.max(
            1e-9,
            ...series
              .flatMap((s) => s.points.map((p) => p.v))
              .filter((v): v is number => Number.isFinite(v as number))
              .map((v) => Math.abs(v)),
          )
        : 0;
    const symmetricAutoscale =
      yFormat === 'index'
        ? () => ({ priceRange: { minValue: -maxAbs, maxValue: maxAbs } })
        : undefined;

    const rendered: {
      label: string;
      api: ISeriesApi<'Baseline' | 'Histogram' | 'Line'>;
    }[] = [];

    series.forEach((s) => {
      const data = toChartData(s);

      if (s.kind === 'bar') {
        const bar = chart.addHistogramSeries({
          priceLineVisible: false,
          ...(symmetricAutoscale ? { autoscaleInfoProvider: symmetricAutoscale } : {}),
        });
        // Only plotted points get a colour; gap points are dropped (a bar gap is
        // simply no bar).
        bar.setData(data.filter(hasValue).map((d) => ({ ...d, color: d.value >= 0 ? buy : sell })));
        rendered.push({ label: s.label, api: bar });
        return;
      }

      if (isMultiLine) {
        // Comparison line — plain distinct colour, no fill.
        const line = chart.addLineSeries({
          color: lineColors.get(s.label),
          lineWidth: 2,
          priceLineVisible: false,
          ...(symmetricAutoscale ? { autoscaleInfoProvider: symmetricAutoscale } : {}),
        });
        line.setData(data);
        rendered.push({ label: s.label, api: line });
      } else {
        // Single signed line — diverging fill against the reference (or 0).
        const line = chart.addBaselineSeries({
          baseValue: { type: 'price', price: reference ?? 0 },
          topLineColor: buy,
          topFillColor1: `${buy}66`,
          topFillColor2: `${buy}0D`,
          bottomLineColor: sell,
          bottomFillColor1: `${sell}66`,
          bottomFillColor2: `${sell}0D`,
          lineWidth: 2,
          priceLineVisible: false,
          ...(symmetricAutoscale ? { autoscaleInfoProvider: symmetricAutoscale } : {}),
        });
        line.setData(data);
        rendered.push({ label: s.label, api: line });
      }
    });

    // Reference line (muted dashed) at meta.reference, on any series (shared axis).
    if (reference != null && rendered.length > 0) {
      rendered[0].api.createPriceLine({
        price: reference,
        color: refColor,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: '',
      });
    }

    seriesRef.current = rendered;
    chart.timeScale().fitContent();

    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time) {
        setTooltip(null);
        return;
      }
      const rows: TooltipRow[] = [];
      rendered.forEach(({ label, api }) => {
        const point = param.seriesData.get(api) as { value?: number } | undefined;
        if (point && typeof point.value === 'number') {
          rows.push({ label, value: formatFlowValue(point.value, yFormat) });
        }
      });
      if (rows.length === 0) {
        setTooltip(null);
        return;
      }
      setTooltip({
        x: param.point.x,
        y: param.point.y,
        date: fmtTooltipDate(param.time as UTCTimestamp, hourly),
        rows,
      });
    });

    const ro = new ResizeObserver(([entry]) => {
      const { width } = entry.contentRect;
      if (width > 0) chart.applyOptions({ width });
    });
    ro.observe(container.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = undefined;
      seriesRef.current = [];
    };
  }, [series, yFormat, reference, height, hourly, resolvedMode, isMultiLine, lineColors]);

  const isDark = resolvedMode === 'dark';
  const tooltipBg = isDark ? '#0d1f2a' : '#ffffff';
  const tooltipBorder = isDark ? '#1f3645' : 'rgba(9, 45, 58, 0.15)';
  const tooltipText = isDark ? '#e8f4fd' : '#092D3A';
  const tooltipMuted = isDark ? 'rgba(232, 244, 253, 0.6)' : 'rgba(9, 45, 58, 0.6)';

  return (
    <Box>
      {isMultiLine && (
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
          {lineSeries.map((s) => (
            <Stack key={s.label} direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 10,
                  height: 2,
                  borderRadius: '1px',
                  backgroundColor: lineColors.get(s.label),
                }}
              />
              <Box sx={{ fontSize: 10, color: tokens.text.secondary }}>{s.label}</Box>
            </Stack>
          ))}
        </Stack>
      )}
      <Box sx={{ position: 'relative', height }}>
        <Box ref={container} sx={{ position: 'relative', width: '100%', height: '100%' }} />
        {tooltip && (
          <Box
            sx={{
              position: 'absolute',
              left: Math.min(tooltip.x + 12, (container.current?.clientWidth ?? 0) - 150),
              top: Math.max(tooltip.y - 52, 4),
              pointerEvents: 'none',
              zIndex: 2,
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              boxShadow: '0px 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <Box sx={{ fontSize: 9, color: tooltipMuted, lineHeight: 1.4 }}>{tooltip.date}</Box>
            {tooltip.rows.map((r) => (
              <Box
                key={r.label}
                sx={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}
              >
                <Box sx={{ fontSize: 10, color: tooltipMuted }}>{r.label}</Box>
                <Box sx={{ fontSize: 11, fontWeight: 700, color: tooltipText }}>{r.value}</Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
